import React, { useLayoutEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import PropTypes from 'prop-types';

// @mui
import { useTheme } from '@mui/material/styles';

// core
import { useGlobal } from '_core/hooks/useGlobal';
import { customCallback } from '_core/utils/customCallback';
import { withVisitorInfo } from '_core/utils/visitorInfo';
import { HistoryListener } from '_core/components/HistoryListener';
import { Loading } from '_core/components/Loading';
import { RedirectHandler } from '_core/components/RedirectHandler';
import { Client } from 'basic/layouts/Client';
import { Guest } from 'basic/layouts/Guest';

const RawRootView = (props) => {
  const { isGuest, isReady, visitorInfo, ...rest } = props;
  const theme = useTheme();

  const {
    config: { ROUTE_URL, VISITOR_REDIRECT, ENABLE_REDIRECT_SELF_GUARD },
    routes
  } = useGlobal();

  React.useEffect(() => {
    RedirectController.checkRedirect(
      VISITOR_REDIRECT,
      visitorInfo,
      window.location.pathname,
      ENABLE_REDIRECT_SELF_GUARD
    );
  }, [visitorInfo]);

  const locationCallback = customCallback(theme.pages.locationCallback);
  const onLocationChange = ({ pathname }) => {
    RedirectController.checkRedirect(
      VISITOR_REDIRECT,
      visitorInfo,
      pathname,
      ENABLE_REDIRECT_SELF_GUARD
    );

    return locationCallback(pathname);
  };

  const renderLayout = () => {
    const Layout = isGuest ? Guest : Client;

    return (
      <HistoryListener onChange={onLocationChange}>
        <Layout
          {...rest}
          render={({ setLayoutProps }) => (
            <Switch>
              <Route path="/redir" component={RedirectHandler} />
              <Route path="/office/redir" component={RedirectHandler} />
              {renderMainRoutes(routes, setLayoutProps)}
              <Redirect to={ROUTE_URL.INDEX} />
            </Switch>
          )}
        />
      </HistoryListener>
    );
  };

  const renderMainRoutes = (list, setLayoutProps) => {
    if (list == null || !(list instanceof Object)) {
      return null;
    }

    return Object.keys(list)
      .map((key) => {
        const {
          Component,
          RouteComponent,
          componentProps,
          layoutProps,
          routeProps,
          privileges
        } = list[key];

        if (!visitorInfo.isAllowed(privileges)) return null;

        return (
          <RouteComponent {...routeProps} key={key}>
            <RouteRender
              layoutProps={layoutProps}
              setLayoutProps={setLayoutProps}
            >
              <RedirectController {...routeProps} visitorInfo={visitorInfo}>
                <Component {...componentProps}>
                  {renderSubRoutes(list[key].routes)}
                </Component>
              </RedirectController>
            </RouteRender>
          </RouteComponent>
        );
      })
      .filter((item) => !!item);
  };

  const renderSubRoutes = (list) => {
    if (list == null || !(list instanceof Object)) {
      return null;
    }

    return Object.keys(list).map((key, index) => {
      const { Component, componentProps, routeProps } = list[key];

      return (
        <Route {...routeProps} key={index}>
          <RedirectController {...routeProps} visitorInfo={visitorInfo}>
            <Component {...componentProps}>
              {renderSubRoutes(list[key].routes)}
            </Component>
          </RedirectController>
        </Route>
      );
    });
  };

  const RouteRender = (props) => {
    const { children, layoutProps, setLayoutProps } = props;

    useLayoutEffect(() => {
      typeof setLayoutProps === 'function' && setLayoutProps(layoutProps);
    }, []);

    return children;
  };

  return isReady ? renderLayout() : <Loading />;
};

RawRootView.defaultProps = {};

RawRootView.propTypes = {
  // self props
  isReady: PropTypes.bool.isRequired,
  isGuest: PropTypes.bool,
  children: PropTypes.node,

  // withVisitorInfo
  visitorInfo: PropTypes.object.isRequired
};

export const RootView = withVisitorInfo(RawRootView);

const RedirectController = (props) => {
  const {
    config: { VISITOR_REDIRECT, ENABLE_REDIRECT_SELF_GUARD }
  } = useGlobal();

  if (
    RedirectController.checkRedirect(
      VISITOR_REDIRECT,
      props.visitorInfo,
      window.location.pathname,
      ENABLE_REDIRECT_SELF_GUARD
    )
  )
    return null;

  return props.children;
};

RedirectController.checkRedirect = (
  VISITOR_REDIRECT,
  visitorInfo,
  pathname,
  enableRedirectSelfGuard = false
) => {
  if (!Array.isArray(VISITOR_REDIRECT)) return false;

  if (!pathname) pathname = '/';

  const urlPrefix = process.env.PUBLIC_URL;

  if (urlPrefix && urlPrefix.length > 1 && pathname.startsWith(urlPrefix))
    pathname = pathname.substring(urlPrefix.length);

  if (pathname.length > 1 && pathname.endsWith('/'))
    pathname = pathname.substring(0, pathname.length - 1);

  return VISITOR_REDIRECT.some((item) => {
    const paths = !item.pathname
      ? null
      : Array.isArray(item.pathname)
      ? item.pathname
      : [item.pathname];

    if (
      paths &&
      !paths.some((path) =>
        path instanceof RegExp ? path.test(pathname) : path === pathname
      )
    )
      return false;

    if (
      item.privileges &&
      (visitorInfo.isLoading || !visitorInfo.isAllowed(item.privileges))
    )
      return false;

    if (enableRedirectSelfGuard) {
      let targetPath = item.to;
      try {
        targetPath = new URL(item.to, window.location.origin).pathname;
      } catch (error) {
        targetPath = item.to;
      }

      if (typeof targetPath === 'string') {
        if (urlPrefix && urlPrefix.length > 1 && targetPath.startsWith(urlPrefix))
          targetPath = targetPath.substring(urlPrefix.length);

        if (!targetPath) targetPath = '/';

        if (targetPath.length > 1 && targetPath.endsWith('/'))
          targetPath = targetPath.substring(0, targetPath.length - 1);

        if (targetPath === pathname) return false;
      }
    }

    window.location.assign(item.to);

    return true;
  });
};
